import React from "react";
import { styled } from "../../../../theme/styled";
import type { ButtonProps, ButtonVariant } from "../../../buttons/Button";
import { Button } from "../../../buttons/Button";
import type { DialogProps } from "../DialogBase";

interface BaseInteractionProps extends InteractionButtonsProps {
  type: "confirmation" | "destructive";
}
export interface InteractionButtonsProps {
  /**
   * Text of the cancel button
   */
  cancelText: string;
  /**
   * Text of the action button
   */
  actionText: string;
  /**
   * Action button can be disabled
   */
  actionDisabled?: boolean;
  /**
   * Confirm function
   */
  onConfirm: NonNullable<ButtonProps["onClick"]>;
  /**
   * Optional cancel function, will be called on cancel button click
   * if not specified, the onClose prop will be called when canceling
   */
  onCancel?: () => void;
}

export const InteractionButtons = (
  props: BaseInteractionProps & Pick<DialogProps, "onClose" | "actionIcon">,
): React.JSX.Element => {
  const variant: ButtonVariant = props.type === "destructive"
    ? "dismissive"
    : "primary";

  const cancelBtn = (
    <Button
      key="cancel"
      size="medium"
      onClick={props.onCancel ?? props.onClose}
      variant="secondary"
    >
      {props.cancelText}
    </Button>
  );

  const actionBtn = (
    <Button
      key="action"
      variant={variant}
      size="medium"
      onClick={props.onConfirm}
      disabled={props.actionDisabled}
    >
      {props.actionIcon && <Button.Icon iconComponent={props.actionIcon} />}
      {props.actionText}
    </Button>
  );

  const actionButtons = [actionBtn, cancelBtn];

  return (
    <InteractionButtonsWrapper>
      {actionButtons.map((btn) => btn)}
    </InteractionButtonsWrapper>
  );
};

export const InteractionButtonsWrapper = styled("div")({
  display: "flex",
  marginTop: "$spacing-40",
  flexDirection: "row-reverse",
  gap: "$spacing-12",
  "& button": {
    minWidth: "130px",
  },
  $xsmall: {
    marginTop: "$spacing-24",
    flexDirection: "column",
  },
});
